// ============================================
// Streaming Mini-Game UI
// Replaces streaming overlay + StoryContentController streaming parts
// ============================================

import { events, Events } from '../engine/events.js';
import { UITags } from '../engine/constants.js';
import { L } from '../systems/localization.js';
import { streamingChatPool } from '../systems/streaming-chat-pool.js';

// ── Default viewer name pool (matches ViewerNameProvider.cs) ────────────
const VIEWER_NAMES = [
    'CutieHunter99', 'SissyLover42', 'NightOwl_x', 'BigTipper420',
    'PinkDreamer', 'xXSissyFanXx', 'PinkPrincess99', 'NightOwl_VIP',
    'GentleDom_', 'YourBiggestFan', 'SubWatcher', 'TipKing2025',
    'LurkerPro', 'ChattyViewer', 'PixelHeart', 'StreamSniper_',
    'EmojiSpam', 'QuietObserver', 'VIPClient', 'AnonViewer',
    'SugarRush99', 'Patron_Angel_', 'NeonLights22', 'PurpleMoon',
    'StarGazer88', 'CozyVibes_', 'RainbowDash_', 'TechGuru404',
    'MidnightRain', 'CherryBlossom', 'SilverFox_VIP', 'GoldenTip',
    'ShyButCurious', 'BoldAndBrave', 'ChillVibes00', 'DarkHorse_',
    'LuckyCharm7', 'SpicyChat_', 'WildCard_', 'ZenMaster_',
];

// ── Chat message templates ──────────────────────────────────────────────
const CHAT_IDLE = [
    'Hey everyone!', 'First time here!', "Let's gooo", 'hi from germany',
    'youre so cute!', 'poggers', 'LETS GO', 'love the outfit!',
    'good vibes tonight', 'hey cutie', 'OMG hi!', 'just got here',
    'whos this?', 'nice stream!', 'fire fit today', 'looking good!',
    'how are you doing today?', 'just subbed!', 'hello from brazil!',
];

const CHAT_HYPE = [
    'YESSSSS', 'OH MY GOD', 'LETS GOOOO', 'no way!!',
    'this is insane', 'CLIP IT', 'AMAZING', 'bruh',
    'someone clip this', 'OMG OMG', 'WOOOO', 'absolute legend',
    'chef kiss', 'S-TIER content', 'PEAK', 'I CANT',
];

const CHAT_TIP = [
    'take my money!', 'deserved!', 'you earned it!',
    'here you go cutie', 'for you!', 'keep going!',
];

// Chat-pool lines resolve per-locale via stream.chat.<pool>.<n> with the EN
// literal above as fallback (locale rows may lag; EN internet slang still
// reads authentic in a stream chat). Viewer NAMES stay latin on purpose —
// usernames don't localize. foundby:Marcus (RU) 0.3.0.3.
function _chatLine(poolKey, fallbacks) {
    const idx = Math.floor(Math.random() * fallbacks.length);
    const key = `stream.chat.${poolKey}.${idx + 1}`;
    try {
        const v = L.get(key);
        if (v && v !== key) return v;
    } catch (e) { /* L10n not ready — EN fallback */ }
    return fallbacks[idx];
}

// ── Streaming action intensity classes ──────────────────────────────────
const ACTION_CLASSES = {
    'Flirt':      'stream-action-safe',
    'Dance':      'stream-action-safe',
    'ASMR':       'stream-action-safe',
    'Tease':      'stream-action-spicy',
    'Strip':      'stream-action-spicy',
    'Cosplay':    'stream-action-spicy',
    'Anal':       'stream-action-extreme',
    'Dildo':      'stream-action-extreme',
    'Lovense':    'stream-action-extreme',
    'Cum':        'stream-action-extreme',
    'Duo Dance':  'stream-action-collab',
    'Obedience':  'stream-action-collab',
    'Fan Meetup': 'stream-action-collab',
};

// ── Streaming action icons ──────────────────────────────────────────────
const ACTION_ICONS = {
    'Flirt':      'assets/images/UI/icon_stream_flirt.png',
    'Dance':      'assets/images/UI/icon_stream_dance.png',
    'ASMR':       'assets/images/UI/icon_stream_asmr.png',
    'Tease':      'assets/images/UI/icon_stream_tease.png',
    'Strip':      'assets/images/UI/icon_stream_strip.png',
    'Cosplay':    'assets/images/UI/icon_stream_cosplay.png',
    'Anal':       'assets/images/UI/icon_stream_anal.png',
    'Dildo':      'assets/images/UI/icon_stream_dildo.png',
    'Lovense':    'assets/images/UI/icon_stream_lovense.png',
    'Cum':        'assets/images/UI/icon_stream_cum.png',
    'Duo Dance':  'assets/images/UI/icon_stream_duo.png',
    'Obedience':  'assets/images/UI/icon_stream_obedience.png',
    'Fan Meetup': 'assets/images/UI/icon_stream_fanmeetup.png',
    'Dare':       'assets/images/UI/icon_stream_dare.png',
    'End':        'assets/images/UI/icon_stream_end.png',
};

// ── Category display ────────────────────────────────────────────────────
// Index matches Ink VAR s_category: 0=Cute, 1=Naughty, 2=Domme,
// 3=Fetish, 4=Cosplay, 5=Public-Risk (v0.2.6 Patreon).
const CATEGORY_KEYS = [
    'stream.cat_cute', 'stream.cat_naughty', 'stream.cat_domme',
    'stream.cat_fetish', 'stream.cat_cosplay', 'stream.cat_public_risk',
];
const CATEGORY_FALLBACKS = [
    'Cute/Chat', 'Naughty', 'Domme Request',
    'Fetish', 'Cosplay', 'Public-Risk',
];
const CATEGORY_CLASSES = [
    'stream-category-cute', 'stream-category-naughty', 'stream-category-domme',
    'stream-category-fetish', 'stream-category-cosplay', 'stream-category-public-risk',
];

class StreamingUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {boolean} */
        this._isLive = false;

        /** @type {boolean} */
        this._isHubMode = false;

        /** @type {number} */
        this._chatTimer = null;

        /** @type {boolean} */
        this._chatActive = false;

        /** @type {Array<HTMLElement>} Active tip animations */
        this._activeTips = [];

        // Stream state read from Ink vars
        this._state = {
            viewers: 0,
            followers: 0,
            tips: 0,
            round: 0,
            maxRounds: 4,
            hype: 0,
            tipGoal: 0,
            tipGoalReached: false,
            category: 0,
            collabPartner: 0,
        };

        this._scope = events.createScope('streaming');
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this._deactivate());
        this._bindEvents();
    }

    /** Detach all event listeners (tests + future re-init paths). */
    destroy() {
        this._scope.destroy();
    }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        this._root = document.getElementById('overlay-streaming');
        if (!this._root) {
            this._root = this._createContainer();
        }
        this._buildUI();
    }

    /** Whether streaming overlay is currently active (live or hub). */
    isActive() {
        return this._isLive || this._isHubMode;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UITags.STREAMING) {
                this._activateLive();
            } else if (mode === UITags.STREAMING_HUB) {
                this._activateHub();
            } else {
                this._deactivate();
            }
        });

        // 0.2.6 streaming rework — reactive chat pool. Ink emits #chat:pool
        // (typically at end of each Stream_Round); pool picks a state-aware
        // line and renders it via addNamedChat. Skip-buffer in the pool
        // prevents the same line from re-firing within 5 picks.
        this._scope.on(Events.TAG_CHAT, (poolName) => {
            // poolName is unused for now (single pool). Future: per-pool
            // routing if we add specialty pools (e.g. doxxing-arc-only).
            if (!this._isLive) return;
            try {
                const pick = streamingChatPool.pick(streamingChatPool.buildState());
                if (pick) {
                    // Pool lines come pre-formatted as "viewer_name: message".
                    // Split into username + message for hash-based colour styling.
                    const colonIdx = pick.text.indexOf(':');
                    if (colonIdx > 0 && colonIdx < 30) {
                        const username = pick.text.slice(0, colonIdx).trim();
                        const message = pick.text.slice(colonIdx + 1).trim();
                        this.addNamedChat(username, message, '');
                    } else {
                        // Fallback: render whole line under a generic anon username
                        this.addNamedChat('anon', pick.text, '');
                    }
                }
            } catch (e) {
                console.warn('[Streaming] chat-pool pick failed:', e?.message || e);
            }
        });

        // Observe streaming vars
        const varMap = {
            's_viewers': 'viewers',
            's_tips': 'tips',
            's_round': 'round',
            's_max_rounds': 'maxRounds',
            's_hype': 'hype',
            's_tip_goal': 'tipGoal',
            's_tip_goal_reached': 'tipGoalReached',
            's_category': 'category',
            's_collab_partner': 'collabPartner',
            'sissy_followers': 'followers',
        };

        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            const field = varMap[name];
            if (field) {
                const oldVal = this._state[field];
                this._state[field] = value;
                this._onStateChange(field, oldVal, value);
            }
        });

        this._scope.on(Events.STORY_CLEAR, () => {
            // Don't deactivate here — #ui: tag on new knot re-fires
        });

        // Route named chat messages from Ink info text during streaming
        // Unity StoryContentController.IsChatLine detects "Username: \"message\"" format
        const NPC_SPEAKERS = ['DancerLily', 'DominantOne', 'JustWatching_xx'];
        // Locale-flexible chat-line parser: matches `Name: "msg"` (EN/DE/ES/IT/PT/...),
        // `Name : « msg »` (FR), `Name: «msg»` (RU/some langs), `Name: 「msg」` (JA/ZH/KO),
        // also tolerates curly quotes from translator overrides.
        const CHAT_LINE_RE = /^([^:«「"'\s][^:«「]{0,28}?)\s*[::]\s*[«"「'“„]([\s\S]+?)[»"」'”‟]\s*$/;
        this._scope.on(Events.STORY_INFO_TEXT, (text, lineId) => {
            if (!this._isLive) return;
            // L10n: pull translated copy when lineId present (non-EN locales)
            const translated = lineId ? L.translateLine(lineId, text) : text;
            const lines = translated.split('\n');
            for (const rawLine of lines) {
                const line = rawLine.trim();
                if (!line) continue;
                const m = line.match(CHAT_LINE_RE);
                if (!m) continue;
                const username = m[1].trim();
                if (!username || username.length > 30) continue;
                if (NPC_SPEAKERS.includes(username)) continue;
                const message = m[2].trim();
                const isRegular = [
                    // Legacy regulars (kept; PatronAngel_ retired in 0.2.6 but
                    // listed for any pre-update saves whose Ink state still emits)
                    'xXSissyFanXx', 'NightOwl_VIP', 'GentleDom_', 'PinkPrincess99',
                    'YourBiggestFan', 'Patron_Angel_', 'PatronAngel_',
                    // 0.2.6 rework — five named persistent regulars
                    'the_doctor_', 'xXFemboi420Xx', 'silent_admirer_77',
                    'sub_in_training', 'bigspender_22',
                ].includes(username);
                this.addNamedChat(username, message, isRegular ? 'stream-chat-regular' : '');
            }
        });
    }

    // ── State Changes ───────────────────────────────────────────────────

    _onStateChange(field, oldVal, newVal) {
        if (!this._isLive && !this._isHubMode) return;

        this._updateHeader();

        if (field === 'viewers' && newVal !== oldVal) {
            const d = newVal - oldVal;
            this._showToast(`${d > 0 ? '+' : ''}${d} ${L.get('stream.viewers')}`, d > 0 ? 'stream-toast-viewer' : 'stream-toast-loss');
        }
        if (field === 'followers' && newVal > oldVal) {
            this._showToast(`+${newVal - oldVal} ${L.get('stream.followers')}`, 'stream-toast-follower');
        }
        if (field === 'tips' && newVal > oldVal) {
            this._animateTip(newVal - oldVal);
        }
        if (field === 'hype') {
            this._updateHype();
        }
    }

    // ── Activation ──────────────────────────────────────────────────────

    // Hydrate _state from current Ink vars before the first render.
    // Without this, _activateLive renders the header from defaults (round=0,
    // category=0/Cute) on a fresh stream where the Ink-side vars were just
    // set inside the same _processStoryContent window — observers fire AFTER
    // the #ui:streaming_hub tag activates the UI, so _state is one tick stale.
    // Foundby: streaming-rework Playwright pass 2026-05-06 — Fetish/Public-Risk
    // streams showed "Live Cute Round 0/4" in the header.
    _hydrateFromInk() {
        try {
            const vs = window._sm?.currentStory?.variablesState;
            if (!vs) return;
            const num = (k) => Number(vs[k] || 0);
            this._state.viewers = num('s_viewers');
            this._state.tips = num('s_tips');
            this._state.round = num('s_round');
            this._state.maxRounds = num('s_max_rounds') || 4;
            this._state.hype = num('s_hype');
            this._state.tipGoal = num('s_tip_goal');
            this._state.tipGoalReached = !!vs['s_tip_goal_reached'];
            this._state.category = num('s_category');
            this._state.collabPartner = num('s_collab_partner');
            this._state.followers = num('sissy_followers');
        } catch (e) {}
    }

    _activateLive() {
        if (!this._root) this.init();
        this._isLive = true;
        this._isHubMode = false;
        this._root.classList.remove('hidden');
        this._root.classList.add('streaming-live');
        this._root.classList.remove('streaming-hub');
        this._hydrateFromInk();
        this._updateHeader();
        this._startChatFlood();
        // 0.2.6 — kick off pool load + clear skip-buffer for the new stream
        streamingChatPool.load();
        streamingChatPool.resetRecent();
    }

    _activateHub() {
        if (!this._root) this.init();
        this._isLive = false;
        this._isHubMode = true;
        this._root.classList.remove('hidden');
        this._root.classList.remove('streaming-live');
        this._root.classList.add('streaming-hub');
        this._hydrateFromInk();
        this._updateHeader();
        this._stopChatFlood();
    }

    _deactivate() {
        this._isLive = false;
        this._isHubMode = false;
        if (this._root) {
            this._root.classList.add('hidden');
            this._root.classList.remove('streaming-live', 'streaming-hub');
        }
        this._stopChatFlood();
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-streaming';
        el.className = 'streaming-overlay hidden';
        // Insert before story content so it sits above
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        return el;
    }

    _buildUI() {
        // v0.2.6.5 layout: split into .stream-main (header + content +
        // tip-anim) and .stream-chat-rail (sibling column). On desktop the
        // rail sits as a 240px right sidebar via flex-row (.streaming-overlay
        // media query). On mobile (<1024px) the rail floats as an absolute
        // overlay, default collapsed so it doesn't eat story space.
        this._root.innerHTML = `
            <div class="stream-main" id="stream-main">
                <div class="stream-header" id="stream-header">
                    <div class="stream-header-row1">
                        <span class="stream-badge stream-badge-live" id="stream-live-badge">\u25CF ${L.get('stream.live')}</span>
                        <span class="stream-badge stream-badge-category" id="stream-category"></span>
                        <span class="stream-stat" id="stream-viewers">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                            <span id="stream-viewer-count">0</span>
                        </span>
                        <span class="stream-stat" id="stream-followers-stat">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                            <span id="stream-follower-count">0</span>
                        </span>
                    </div>
                    <div class="stream-header-row2" id="stream-row2">
                        <span class="stream-round" id="stream-round">${L.get('stream.round')} 1/4</span>
                        <div class="stream-tip-goal" id="stream-tip-goal">
                            <span class="stream-tip-goal-label" id="stream-tip-label"></span>
                            <div class="stream-tip-goal-bar">
                                <div class="stream-tip-goal-fill" id="stream-tip-fill"></div>
                            </div>
                        </div>
                        <span class="stream-hype" id="stream-hype"></span>
                        <span class="stream-collab" id="stream-collab"></span>
                    </div>
                </div>
                <div class="stream-tip-container" id="stream-tip-anim"></div>
            </div>
            <div class="stream-chat-rail" id="stream-chat-rail">
                <div class="stream-chat-header">
                    <button class="stream-chat-toggle" id="stream-chat-toggle">${L.get('stream.chat')} <span id="stream-chat-badge" class="stream-chat-badge hidden">0</span></button>
                </div>
                <div class="stream-chat" id="stream-chat"></div>
            </div>
        `;

        // Default-collapsed rail on mobile so chat doesn't eat story room
        // until the player opts in. Desktop (\u22651024px) keeps it expanded by
        // default \u2014 the rail has its own column there.
        const rail = this._root.querySelector('#stream-chat-rail');
        if (rail && window.innerWidth < 1024) {
            rail.classList.add('stream-chat-collapsed');
        }

        // Chat toggle \u2014 clicks the header button OR the header itself on
        // mobile (entire bar is the touch target for easy thumb access).
        const chatToggle = this._root.querySelector('#stream-chat-toggle');
        const header = this._root.querySelector('.stream-chat-rail .stream-chat-header');
        const onToggle = () => {
            if (!rail) return;
            const wasCollapsed = rail.classList.contains('stream-chat-collapsed');
            rail.classList.toggle('stream-chat-collapsed');
            if (chatToggle) chatToggle.classList.toggle('stream-chat-collapsed', !wasCollapsed);
            // Reset badge when chat is opened
            if (wasCollapsed) {
                this._chatBadgeCount = 0;
                const badge = this._root?.querySelector('#stream-chat-badge');
                if (badge) {
                    badge.textContent = '0';
                    badge.classList.add('hidden');
                }
            }
        };
        if (chatToggle) chatToggle.addEventListener('click', (e) => { e.stopPropagation(); onToggle(); });
        if (header) header.addEventListener('click', (e) => {
            // Avoid double-fire when the inner toggle button was the click target
            if (e.target.closest('#stream-chat-toggle')) return;
            onToggle();
        });
    }

    _updateChatBadge() {
        const badge = this._root?.querySelector('#stream-chat-badge');
        if (!badge) return;
        const chat = this._root?.querySelector('#stream-chat');
        if (chat && chat.classList.contains('hidden')) {
            this._chatBadgeCount = (this._chatBadgeCount || 0) + 1;
            badge.textContent = this._chatBadgeCount;
            badge.classList.remove('hidden');
        }
    }

    // ── Header Updates ──────────────────────────────────────────────────

    _updateHeader() {
        if (!this._root) return;
        const s = this._state;

        // Live/Offline badge
        const badge = this._root.querySelector('#stream-live-badge');
        if (badge) {
            if (this._isLive) {
                badge.textContent = '\u25CF ' + L.get('stream.live');
                badge.className = 'stream-badge stream-badge-live';
            } else {
                badge.textContent = L.get('stream.offline');
                badge.className = 'stream-badge stream-badge-offline';
            }
        }

        // Category
        const cat = this._root.querySelector('#stream-category');
        if (cat) {
            const catKey = CATEGORY_KEYS[s.category];
            cat.textContent = catKey ? (L.get(catKey) || CATEGORY_FALLBACKS[s.category] || '') : '';
            cat.className = 'stream-badge stream-badge-category ' + (CATEGORY_CLASSES[s.category] || '');
            cat.classList.toggle('hidden', !this._isLive);
        }

        // Viewers
        const vc = this._root.querySelector('#stream-viewer-count');
        if (vc) vc.textContent = s.viewers;
        const viewerStat = this._root.querySelector('#stream-viewers');
        if (viewerStat) viewerStat.classList.toggle('hidden', !this._isLive);

        // Followers
        const fc = this._root.querySelector('#stream-follower-count');
        if (fc) fc.textContent = s.followers;

        // Row 2 (live only)
        const row2 = this._root.querySelector('#stream-row2');
        if (row2) row2.classList.toggle('hidden', !this._isLive);

        // Round
        const round = this._root.querySelector('#stream-round');
        if (round) round.textContent = `${L.get('stream.round')} ${s.round}/${s.maxRounds}`;

        // Tip goal
        const tipGoalEl = this._root.querySelector('#stream-tip-goal');
        if (tipGoalEl) {
            tipGoalEl.classList.toggle('hidden', s.tipGoal <= 0);
        }
        const tipLabel = this._root.querySelector('#stream-tip-label');
        if (tipLabel) tipLabel.textContent = `$${s.tips} / $${s.tipGoal}`;
        const tipFill = this._root.querySelector('#stream-tip-fill');
        if (tipFill) {
            const pct = s.tipGoal > 0 ? Math.min(100, (s.tips / s.tipGoal) * 100) : 0;
            tipFill.style.width = `${pct}%`;
            tipFill.classList.toggle('stream-tip-goal-complete', s.tipGoalReached);
        }

        // Collab
        const collab = this._root.querySelector('#stream-collab');
        if (collab) {
            const masterName = window._sm?.currentStory?.variablesState?.['MasterName'] || 'Master';
            const names = ['', 'Lily', masterName, 'PinkPrincess99'];
            const name = names[s.collabPartner] || '';
            collab.textContent = name ? `${L.get('stream.collab')}: ${name}` : '';
            collab.classList.toggle('hidden', !name);
        }

        this._updateHype();
    }

    _updateHype() {
        const el = this._root.querySelector('#stream-hype');
        if (!el) return;
        const h = this._state.hype;
        if (h > 3) {
            el.textContent = '\uD83D\uDD25'.repeat(Math.min(5, h));
        } else if (h >= 1) {
            el.textContent = '\uD83D\uDD25'.repeat(h);
        } else if (h < -2) {
            el.textContent = '\u2744\uFE0F\u2744\uFE0F';
        } else if (h < 0) {
            el.textContent = '\u2744\uFE0F';
        } else {
            el.textContent = '';
        }
    }

    // ── Chat Overlay ────────────────────────────────────────────────────

    _startChatFlood() {
        this._stopChatFlood();
        const chat = this._root?.querySelector('#stream-chat');
        if (!chat) return;
        chat.innerHTML = '';

        // Initial burst
        for (let i = 0; i < 3; i++) {
            this._addChatMessage(chat, false);
        }

        // Ongoing messages with variable rate (recursive setTimeout avoids nested setInterval leaks)
        const tick = () => {
            if (!this._chatActive) return;
            this._addChatMessage(chat);
            const rate = this._state.hype > 3 ? 800 : this._state.hype > 0 ? 1500 : 2500;
            this._chatTimer = setTimeout(tick, rate);
        };
        this._chatActive = true;
        this._chatTimer = setTimeout(tick, 2000);
    }

    _stopChatFlood() {
        this._chatActive = false;
        if (this._chatTimer) {
            clearTimeout(this._chatTimer);
            this._chatTimer = null;
        }
    }

    _addChatMessage(chatEl, animate = true) {
        if (!chatEl) return;

        const name = VIEWER_NAMES[Math.floor(Math.random() * VIEWER_NAMES.length)];
        const isHype = this._state.hype > 2 && Math.random() < 0.3;
        // Chat flood is flavour only — never invent dollar amounts. Real
        // tips fire via _animateTip() / addTipChat() when the Ink s_tips
        // variable actually changes (foundby:VPS report 2026-04-26 v0.2.4.2:
        // fake "$X tipped" messages had no Ink-side counterpart, so players
        // saw "$200 in tips" in chat and got the real (much smaller) total
        // at the end with Stream Flop).
        const isTip = false;
        const text = isHype ? _chatLine('hype', CHAT_HYPE) : _chatLine('idle', CHAT_IDLE);

        // Consistent color per username (hash-based)
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
        const hue = Math.abs(hash) % 360;

        // Regular viewer badge
        const REGULARS = ['xXSissyFanXx', 'NightOwl_VIP', 'Patron_Angel_', 'SilverFox_VIP', 'GoldenTip'];
        const isRegular = REGULARS.includes(name);

        const msg = document.createElement('div');
        msg.className = 'stream-chat-bubble' + (animate ? ' stream-chat-enter' : '') + (isTip ? ' stream-chat-tip-msg' : '');
        msg.innerHTML = `
            ${isRegular ? '<span class="stream-chat-badge-regular">\u2B50</span>' : ''}
            <span class="stream-chat-username" style="color:hsl(${hue},70%,65%)">${name}</span>
            <span class="stream-chat-message">${text}</span>
        `;

        chatEl.appendChild(msg);
        this._updateChatBadge();

        // Limit visible messages
        while (chatEl.children.length > 30) {
            chatEl.removeChild(chatEl.firstChild);
        }

        chatEl.scrollTop = chatEl.scrollHeight;
    }

    /** Add a named chat message (for regular viewers, events, etc.) */
    addNamedChat(name, text, cssClass) {
        const chat = this._root ? this._root.querySelector('#stream-chat') : null;
        if (!chat) return;

        // Consistent color per username (hash-based)
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
        const hue = Math.abs(hash) % 360;

        const msg = document.createElement('div');
        msg.className = 'stream-chat-bubble stream-chat-enter ' + (cssClass || '');
        msg.innerHTML = `
            <span class="stream-chat-username" style="color:hsl(${hue},70%,65%)">${name}</span>
            <span class="stream-chat-message">${text}</span>
        `;

        chat.appendChild(msg);
        this._updateChatBadge();
        while (chat.children.length > 30) {
            chat.removeChild(chat.firstChild);
        }
        chat.scrollTop = chat.scrollHeight;
    }

    /** Add a tip message in chat */
    addTipChat(name, amount) {
        const chat = this._root ? this._root.querySelector('#stream-chat') : null;
        if (!chat) return;

        const tipText = _chatLine('tip', CHAT_TIP);
        let tippedTmpl = 'tipped ${amount}!';
        try {
            const v = L.get('stream.chat.tipped');
            if (v && v !== 'stream.chat.tipped') tippedTmpl = v;
        } catch (e) { /* EN fallback */ }
        const msg = document.createElement('div');
        msg.className = 'stream-chat-bubble stream-chat-enter stream-chat-tip';
        msg.innerHTML = `
            <span class="stream-chat-username">${name}</span>
            <span class="stream-chat-message">${tippedTmpl.replace('{amount}', amount)} ${tipText}</span>
        `;

        chat.appendChild(msg);
        this._updateChatBadge();
        while (chat.children.length > 30) {
            chat.removeChild(chat.firstChild);
        }
        chat.scrollTop = chat.scrollHeight;
    }

    // ── Tip Animation ───────────────────────────────────────────────────

    _animateTip(amount) {
        const container = this._root ? this._root.querySelector('#stream-tip-anim') : null;
        if (!container) return;

        const tip = document.createElement('div');
        tip.className = 'stream-tip-float';
        tip.textContent = `+$${amount}`;

        // Randomize horizontal position
        tip.style.left = `${30 + Math.random() * 40}%`;

        container.appendChild(tip);

        // Trigger animation
        requestAnimationFrame(() => tip.classList.add('stream-tip-animate'));

        // Cleanup
        setTimeout(() => tip.remove(), 2000);

        // Also add to chat
        const name = VIEWER_NAMES[Math.floor(Math.random() * VIEWER_NAMES.length)];
        this.addTipChat(name, amount);
    }

    // ── Toast ───────────────────────────────────────────────────────────

    _showToast(text, cssClass) {
        events.emit(Events.TOAST_REQUEST, text, cssClass);
    }

    // ── Utility: Get action intensity class ─────────────────────────────

    /** Returns CSS class for a streaming action choice. */
    getActionClass(actionText) {
        for (const [key, cls] of Object.entries(ACTION_CLASSES)) {
            if (actionText.toLowerCase().includes(key.toLowerCase())) {
                return cls;
            }
        }
        return '';
    }

    /** Returns icon path for a streaming action choice, or fallback default. */
    getActionIcon(actionText) {
        const lower = actionText.toLowerCase();
        for (const [key, path] of Object.entries(ACTION_ICONS)) {
            if (lower.includes(key.toLowerCase())) {
                return path;
            }
        }
        // Fallback: "end" keyword check for End Stream
        if (lower.includes('end') && lower.includes('stream')) {
            return ACTION_ICONS['End'];
        }
        return 'assets/images/UI/icon_stream_default.png';
    }
}

/** @type {StreamingUI} */
export const streamingUI = new StreamingUI();
